import { grey } from "@mui/material/colors";
import { createSxStyles } from "../../createSxStyles";
import { PDF_TEXT_SELECTION_DOM_CONFIG, TEXT_VIEW_TEXT_SELECTION_DOM_CONFIG, WORD_TEXT_SELECTION_DOM_CONFIG } from "./textSelection/textSelection.domConfigs";

const viewerContainer = {
    flex: 1,
    overflow: "auto",
    display: "flex",
    // "safe" rather than plain centring: a page wider than the viewer (a Word page at its real paper
    // width, a portrait PDF in a narrow panel) would otherwise have half its overflow pushed to the left,
    // where scrollLeft bottoms out at 0 and the content is unreachable. Safe centring falls back to start
    // alignment exactly when the content overflows, so everything stays scrollable.
    justifyContent: "safe center",
    alignItems: "flex-start",
    backgroundColor: grey[100],
    borderBottom: "1px solid",
    borderColor: "divider",
    position: "relative",
};

// A drag that starts on the page margin would otherwise anchor at the very start of the document and
// select everything down to the pointer. Making only the text layer selectable makes such a drag start
// at the first selectable position the pointer actually reaches. The selector comes from the same dom
// config the capture code reads, so a renderer upgrade cannot leave the two disagreeing.
const selectableViewerContainer = (textRootSelector: string) => ({
    ...viewerContainer,
    userSelect: "none",
    [`& ${textRootSelector}`]: { userSelect: "text" },
});

const loadingOverlay = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    position: "absolute",
    inset: 0,
    zIndex: 1,
};

export const useDocumentPreviewStyles = (isCompact = false) => {
    return createSxStyles({
        dialogContent: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
        },
        /** The column the viewer fills, in the dialog and in a pane alike. */
        viewerColumn: {
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        },
        // Three tracks rather than a centred row with absolutely positioned ends: the document-navigation
        // and action buttons used to reserve no width, so at rail width the controls ran underneath them.
        //
        // The middle track must be 1fr, never an intrinsic size. A multi-line flex container does not report
        // the sum of its items as its max-content width, so sizing the track from its content gave it far less
        // than the controls need and wrapped them even on a full-width stage.
        toolbar: {
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr) auto",
            alignItems: "center",
            columnGap: 1,
            padding: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
        },
        toolbarSide: {
            display: "flex",
            alignItems: "center",
        },
        toolbarSideEnd: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
        },
        // Wrapping is what lets the toolbar fit the rail: a narrow column has vertical space to spare and
        // none to give horizontally. A wide toolbar has nothing to wrap, so this costs the stage nothing.
        // Compact widens the gap between groups, which is what tells them apart once the dividers are gone.
        toolbarControls: {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            columnGap: isCompact ? 2 : 1,
            rowGap: 1,
        },
        // A group reflows whole. Letting a line break fall inside one would strand a field from the buttons
        // that drive it.
        toolbarCluster: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
        },
        // Compact is the only mode that lets the group grow: it is alone on its line there, so filling the
        // line is what the space is for. On one row growing would swallow the slack the row is centred with.
        toolbarSearchCluster: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: isCompact ? "1 1 auto" : "0 1 auto",
        },
        toolbarSearchField: isCompact ? { flex: "1 1 400px", minWidth: 120, "& input": { fontSize: 14 } } : { width: 400, "& input": { fontSize: 14 } },
        toolbarSearchMatches: {
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
        },
        viewerContainer,
        loadingOverlay,
        pdfViewerContainer: selectableViewerContainer(PDF_TEXT_SELECTION_DOM_CONFIG.textRootSelector),
        wordViewerContainer: selectableViewerContainer(WORD_TEXT_SELECTION_DOM_CONFIG.textRootSelector),
        noticedViewerContainer: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
        },
        noticedViewer: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
        },
        textViewContainer: selectableViewerContainer(TEXT_VIEW_TEXT_SELECTION_DOM_CONFIG.textRootSelector),
        pdfContainer: {
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
        },
        imageViewerContainer: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
        },
        imageContainer: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 2,
            height: "100%",
            width: "100%",
        },
        previewImage: {
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transition: "transform 0.2s ease-in-out",
        },
        loadingContainer: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: 400,
        },
        errorContainer: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: 400,
            gap: 2,
        },
        zoomText: {
            minWidth: 60,
            textAlign: "center",
        },
        pageWrapper: {
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            backgroundColor: "white",
            "&:not(:last-child)": {
                marginBottom: 3,
            },
        },
        emailViewerContainer: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
        },
        emailSheet: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 900,
            minHeight: "100%",
            backgroundColor: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            padding: 3,
            gap: 2,
        },
        emailSubject: {
            fontSize: (theme) => theme.typography.pxToRem(18),
            fontWeight: 600,
            color: "primary.dark",
        },
        emailHeaderFields: {
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: 2,
            rowGap: 0.5,
            paddingBottom: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
        },
        emailHeaderLabel: {
            fontWeight: 600,
            color: "text.secondary",
            whiteSpace: "nowrap",
        },
        emailHeaderValue: {
            color: "text.primary",
            wordBreak: "break-word",
        },
        emailNotice: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            padding: 1,
            borderRadius: 1,
            backgroundColor: grey[100],
            color: "text.secondary",
            fontSize: (theme) => theme.typography.pxToRem(13),
        },
        emailBodyFrame: {
            border: "none",
            width: "100%",
            flex: 1,
            minHeight: 400,
        },
        emailBodyText: {
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "monospace",
            fontSize: (theme) => theme.typography.pxToRem(13),
        },
        emailAttachments: {
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            paddingTop: 2,
            borderTop: "1px solid",
            borderColor: "divider",
        },
    });
};
